import { useState } from 'react';
import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  Select,
  TextField,
  FormLayout,
  Modal,
  Spinner,
  DataTable,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import {
  useUsers,
  useCreateUser,
  useDeleteUser,
  useSetRole,
  type ManagedUser,
} from '@/queries/users';
import { ApiError } from '@/types/api';

const ROLE_OPTIONS = [
  { label: 'Admin', value: 'admin' },
  { label: 'Editor', value: 'editor' },
];

export function UsersPage() {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const isAdmin = admin?.role === 'admin';

  const { data, isLoading, error } = useUsers();
  const createUser = useCreateUser();
  const deleteUser = useDeleteUser();
  const setRole = useSetRole();

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole_] = useState('editor');
  const [formErr, setFormErr] = useState<string | null>(null);

  if (!isAdmin) {
    return (
      <Page title="Users" backAction={{ content: 'Editor', onAction: () => navigate('/') }}>
        <Banner tone="critical" title="Admins only">
          <p>Only an admin can manage users.</p>
        </Banner>
      </Page>
    );
  }

  const submit = () => {
    setFormErr(null);
    createUser.mutate(
      { email, password, role: role as 'admin' | 'editor' },
      {
        onSuccess: () => {
          setOpen(false);
          setEmail('');
          setPassword('');
          setRole_('editor');
        },
        onError: (e: unknown) =>
          setFormErr(e instanceof ApiError ? e.message : 'Could not create user'),
      },
    );
  };

  const rows =
    data?.users.map((u: ManagedUser) => [
      u.email,
      <Select
        key={`r-${u.id}`}
        label=""
        labelHidden
        options={ROLE_OPTIONS}
        value={u.role}
        disabled={u.is_main || setRole.isPending}
        onChange={(v) => setRole.mutate({ id: u.id, role: v as 'admin' | 'editor' })}
      />,
      u.is_main ? <Badge tone="success">Main (approver)</Badge> : <Badge>Member</Badge>,
      u.email === admin?.email ? (
        <Text as="span" tone="subdued">You</Text>
      ) : u.is_main ? (
        <Text as="span" tone="subdued">Protected</Text>
      ) : (
        <Button
          variant="plain"
          tone="critical"
          loading={deleteUser.isPending}
          onClick={() => {
            if (window.confirm(`Delete ${u.email}?`)) deleteUser.mutate(u.id);
          }}
        >
          Delete
        </Button>
      ),
    ]) ?? [];

  return (
    <Page
      title="Users"
      backAction={{ content: 'Editor', onAction: () => navigate('/') }}
      primaryAction={{ content: 'Create user', onAction: () => setOpen(true) }}
    >
      <BlockStack gap="400">
        {error ? (
          <Banner tone="critical" title="Could not load users">
            <p>{error instanceof ApiError ? error.message : 'Unknown error'}</p>
          </Banner>
        ) : null}
        <Card>
          {isLoading ? (
            <InlineStack align="center"><Spinner size="small" /></InlineStack>
          ) : (
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text']}
              headings={['Email', 'Role', 'Type', '']}
              rows={rows}
            />
          )}
        </Card>
        <Text as="p" tone="subdued" variant="bodySm">
          Every user can edit everything. Only the Main account publishes
          directly; everyone else's publish is sent to the Main account for
          approval.
        </Text>
      </BlockStack>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Create user"
        primaryAction={{
          content: 'Create',
          onAction: submit,
          loading: createUser.isPending,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setOpen(false) }]}
      >
        <Modal.Section>
          <FormLayout>
            {formErr ? (
              <Banner tone="critical">{formErr}</Banner>
            ) : null}
            <TextField
              label="Email"
              type="email"
              autoComplete="off"
              value={email}
              onChange={setEmail}
            />
            <TextField
              label="Password"
              type="password"
              autoComplete="off"
              value={password}
              onChange={setPassword}
              helpText="At least 8 characters."
            />
            <Select
              label="Role"
              options={ROLE_OPTIONS}
              value={role}
              onChange={setRole_}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
